import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MediaUploader, MediaDisplay } from "@/components/MediaComponents";
import { 
  Heart, Plus, Edit2, Trash2, Target, Calendar, Users, DollarSign,
  TrendingUp, Star, Receipt, X
} from "lucide-react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    goal: 10000,
    end_date: "",
    image_url: "",
    media: [],
    is_featured: false,
    manual_raised_offset: 0
  });

  // Manual donation state
  const [donationDialogOpen, setDonationDialogOpen] = useState(false);
  const [donationCampaign, setDonationCampaign] = useState(null);
  const [donationForm, setDonationForm] = useState({ donor_name: "", donor_email: "", amount: "", payment_method: "manual", notes: "" });
  const [savingDonation, setSavingDonation] = useState(false);
  const [donationHistory, setDonationHistory] = useState(null);

  const openDonationDialog = async (campaign) => {
    setDonationCampaign(campaign);
    setDonationForm({ donor_name: "", donor_email: "", amount: "", payment_method: "manual", notes: "" });
    setDonationDialogOpen(true);
    try {
      const res = await axios.get(`${API}/admin/campaigns/${campaign.campaign_id}/donations`, { withCredentials: true });
      setDonationHistory(res.data);
    } catch { setDonationHistory(null); }
  };

  const submitManualDonation = async (e) => {
    e.preventDefault();
    if (!donationForm.donor_name.trim() || !donationForm.amount) { toast.error("Donor name and amount are required"); return; }
    setSavingDonation(true);
    try {
      await axios.post(
        `${API}/admin/campaigns/${donationCampaign.campaign_id}/manual-donation`,
        { ...donationForm, amount: parseFloat(donationForm.amount) },
        { withCredentials: true }
      );
      toast.success(`Recorded $${parseFloat(donationForm.amount).toFixed(2)} from ${donationForm.donor_name}`);
      // refresh history + campaigns
      const [hist] = await Promise.all([
        axios.get(`${API}/admin/campaigns/${donationCampaign.campaign_id}/donations`, { withCredentials: true }),
        fetchCampaigns(),
      ]);
      setDonationHistory(hist.data);
      setDonationForm({ donor_name: "", donor_email: "", amount: "", payment_method: "manual", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record donation");
    } finally { setSavingDonation(false); }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns?active_only=false`, { withCredentials: true });
      setCampaigns(response.data);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (isEditing) {
        await axios.put(`${API}/campaigns/${selectedCampaign.campaign_id}`, formData, { withCredentials: true });
        toast.success("Campaign updated successfully!");
      } else {
        await axios.post(`${API}/campaigns`, formData, { withCredentials: true });
        toast.success("Campaign created successfully!");
      }
      setDialogOpen(false);
      resetForm();
      fetchCampaigns();
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error("Failed to save campaign");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/campaigns/${selectedCampaign.campaign_id}`, { withCredentials: true });
      toast.success("Campaign deactivated");
      setDeleteDialogOpen(false);
      fetchCampaigns();
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
    }
  };

  const openEditDialog = (campaign) => {
    setSelectedCampaign(campaign);
    setFormData({
      title: campaign.title,
      description: campaign.description || "",
      goal: campaign.goal,
      end_date: campaign.end_date || "",
      image_url: campaign.image_url || "",
      media: campaign.media || [],
      is_featured: campaign.is_featured || false,
      manual_raised_offset: campaign.manual_raised_offset || 0
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsEditing(false);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      goal: 10000,
      end_date: "",
      image_url: "",
      is_featured: false,
      manual_raised_offset: 0
    });
    setSelectedCampaign(null);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const totalRaised = campaigns.reduce((sum, c) => sum + (c.raised || 0), 0);
  const totalGoal = campaigns.reduce((sum, c) => sum + (c.goal || 0), 0);

  return (
    <AdminLayout currentPage="Campaigns">
      <div className="p-8" data-testid="admin-campaigns">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Donation Campaigns</h1>
            <p className="text-slate-600">Create and manage fundraising campaigns</p>
          </div>
          <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-campaign-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-awareness-blue/10 rounded-full flex items-center justify-center">
                <Target className="w-5 h-5 text-awareness-blue" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Active Campaigns</p>
                <p className="text-2xl font-bold text-deep-blue">
                  {campaigns.filter(c => c.is_active).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Raised</p>
                <p className="text-2xl font-bold text-deep-blue">{formatCurrency(totalRaised)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Combined Goal</p>
                <p className="text-2xl font-bold text-deep-blue">{formatCurrency(totalGoal)}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Donors</p>
                <p className="text-2xl font-bold text-deep-blue">
                  {campaigns.reduce((sum, c) => sum + (c.donor_count || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 bg-slate-100 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : campaigns.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map(campaign => (
              <div 
                key={campaign.campaign_id} 
                className={`bg-white rounded-lg shadow-sm overflow-hidden border ${!campaign.is_active ? 'opacity-60' : ''}`}
                data-testid={`campaign-${campaign.campaign_id}`}
              >
                {campaign.is_featured && (
                  <div className="bg-awareness-blue text-white text-xs font-semibold px-3 py-1 flex items-center gap-1">
                    <Star className="w-3 h-3" /> Featured Campaign
                  </div>
                )}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-bold text-deep-blue text-lg">{campaign.title}</h3>
                      {campaign.end_date && (
                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                          <Calendar className="w-4 h-4" />
                          Ends {format(new Date(campaign.end_date), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(campaign)}
                        data-testid={`edit-campaign-${campaign.campaign_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setSelectedCampaign(campaign); setDeleteDialogOpen(true); }}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{campaign.description}</p>
                  
                  <div className="mb-3">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-semibold text-awareness-blue">{formatCurrency(campaign.raised || 0)}</span>
                      <span className="text-slate-500">of {formatCurrency(campaign.goal)}</span>
                    </div>
                    <Progress value={campaign.progress_percent || 0} className="h-2" />
                  </div>
                  
                  <div className="flex justify-between items-center text-sm text-slate-500">
                    <span>{campaign.donor_count || 0} donors</span>
                    <span>{Math.round(campaign.progress_percent || 0)}% funded</span>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => openDonationDialog(campaign)}
                    data-testid={`record-donation-${campaign.campaign_id}`}
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Record / View Donations
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Heart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No campaigns yet</h3>
            <p className="text-slate-500 mb-4">Create your first donation campaign to start fundraising</p>
            <Button className="btn-primary" onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{isEditing ? "Edit Campaign" : "Create Campaign"}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Campaign Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="Annual Awareness Fund"
                  required
                  data-testid="campaign-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Help us spread prostate cancer awareness..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="goal">Goal Amount ($)</Label>
                  <Input
                    id="goal"
                    type="number"
                    value={formData.goal}
                    onChange={(e) => setFormData({...formData, goal: parseInt(e.target.value) || 0})}
                    min={100}
                    data-testid="campaign-goal-input"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="manual_raised_offset">Offline / Pre-existing Raised Amount ($)</Label>
                <Input
                  id="manual_raised_offset"
                  type="number"
                  value={formData.manual_raised_offset}
                  onChange={(e) => setFormData({...formData, manual_raised_offset: parseInt(e.target.value) || 0})}
                  min={0}
                  placeholder="e.g. 9000 for $9,000 already raised offline"
                  data-testid="campaign-offset-input"
                />
                <p className="text-xs text-slate-500 mt-1">Add any donations collected outside the website (cash, checks, prior fundraising). This adds to live online donations.</p>
              </div>

              <MediaUploader value={formData.media || []} onChange={(m) => setFormData(prev => ({...prev, media: m}))} label="Campaign Media (photos/video slideshow)" />

              <div className="flex items-center justify-between">
                <Label htmlFor="is_featured">Feature this campaign</Label>
                <Switch
                  id="is_featured"
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="submit-campaign">
                  {isEditing ? "Save Changes" : "Create Campaign"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate Campaign</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to deactivate "{selectedCampaign?.title}"? 
                The campaign will no longer be visible to donors, but donation history will be preserved.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Manual Donation Dialog */}
        <Dialog open={donationDialogOpen} onOpenChange={setDonationDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="donation-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-awareness-blue" />
                {donationCampaign?.title} — Donations
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* Record new donation */}
              <form onSubmit={submitManualDonation} className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
                <h3 className="font-semibold text-deep-blue">Record an Offline Donation</h3>
                <p className="text-xs text-slate-500 -mt-2">Use this for cash, checks, wires, or any donation collected outside the website.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="donor_name">Donor Name *</Label>
                    <Input id="donor_name" value={donationForm.donor_name} onChange={(e) => setDonationForm({...donationForm, donor_name: e.target.value})} placeholder="John Smith" required data-testid="donor-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="donor_email">Donor Email</Label>
                    <Input id="donor_email" type="email" value={donationForm.donor_email} onChange={(e) => setDonationForm({...donationForm, donor_email: e.target.value})} placeholder="optional" data-testid="donor-email-input" />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount (USD) *</Label>
                    <Input id="amount" type="number" step="0.01" min="0.01" value={donationForm.amount} onChange={(e) => setDonationForm({...donationForm, amount: e.target.value})} placeholder="500" required data-testid="donor-amount-input" />
                  </div>
                  <div>
                    <Label htmlFor="payment_method">Payment Method</Label>
                    <select
                      id="payment_method"
                      value={donationForm.payment_method}
                      onChange={(e) => setDonationForm({...donationForm, payment_method: e.target.value})}
                      className="w-full border border-input rounded-md h-10 px-3 text-sm bg-background"
                      data-testid="donor-method-input"
                    >
                      <option value="manual">Other / Manual</option>
                      <option value="cash">Cash</option>
                      <option value="check">Check</option>
                      <option value="wire">Bank Wire</option>
                      <option value="venmo">Venmo / Cash App</option>
                      <option value="paypal">PayPal (offline)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Input id="notes" value={donationForm.notes} onChange={(e) => setDonationForm({...donationForm, notes: e.target.value})} placeholder="Check #1234, received Mar 5" data-testid="donor-notes-input" />
                </div>
                <Button type="submit" className="bg-deep-blue text-white" disabled={savingDonation} data-testid="save-donation-btn">
                  {savingDonation ? "Saving..." : <><Plus className="w-4 h-4 mr-2" /> Record Donation</>}
                </Button>
              </form>

              {/* Donation history */}
              <div>
                <h3 className="font-semibold text-deep-blue mb-2 flex items-center justify-between">
                  <span>All Donations</span>
                  {donationHistory && <span className="text-sm font-normal text-slate-500">{donationHistory.count} • {formatCurrency(donationHistory.total)}</span>}
                </h3>
                {!donationHistory ? (
                  <p className="text-sm text-slate-400">Loading...</p>
                ) : donationHistory.donations.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No donations recorded yet.</p>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-slate-600 text-xs uppercase">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Donor</th>
                          <th className="px-3 py-2 text-left">Method</th>
                          <th className="px-3 py-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {donationHistory.donations.map(d => (
                          <tr key={d.donation_id}>
                            <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{d.created_at ? format(new Date(d.created_at), "MMM d, yyyy") : ""}</td>
                            <td className="px-3 py-2 text-slate-800">
                              <div className="font-medium">{d.donor_name || "Anonymous"}</div>
                              {d.donor_email && <div className="text-xs text-slate-400">{d.donor_email}</div>}
                            </td>
                            <td className="px-3 py-2 text-slate-600 text-xs capitalize">{d.payment_method || (d.is_manual ? "manual" : "online")}</td>
                            <td className="px-3 py-2 text-right font-semibold text-deep-blue">{formatCurrency(d.amount || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setDonationDialogOpen(false)}><X className="w-4 h-4 mr-2" /> Close</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
